import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik, Form, FormikProvider, FieldArray } from "formik";
import * as Yup from "yup";
import {
  createSubscription,
  updateSubscription,
  getSubscriptionsList,
} from "../../appRedux/actions/subscriptions";
import { getPlatformsList } from "../../appRedux/actions/platforms";
import { hideDialog as hideDialogCommon } from "../../appRedux/actions/common";
import config from "../../utils/config";
import { Table, Modal, Button, Space, Switch, Tag, Input } from "antd";

const STATUSES = [
  { value: "DRAFT", label: "Draft" },
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];

const BILLING_CYCLES = [
  { value: "MONTHLY", label: "Monthly" },
  { value: "QUARTERLY", label: "Quarterly" },
  { value: "YEARLY", label: "Yearly" },
  { value: "CUSTOM", label: "Custom days" },
];

const DISCOUNT_TYPES = [
  { value: "PERCENT", label: "Percentage" },
  { value: "FLAT", label: "Flat amount" },
];

const INITIAL_OPTION = {
  name: "",
  priceType: "PAID",
  price: "",
  currency: "INR",
  billingCycle: "MONTHLY",
  customCycleDays: "",
  trialPeriodDays: 0,
  guaranteePeriodDays: 0,
  buttonText: "",
  ctaLink: "",
  subtext: "",
  featuresHeader: "",
  isPopular: false,
  features: [],
  isActive: true,
  discountType: "",
  discountValue: "",
  startAt: "",
  endAt: "",
};

const INITIAL_FAQ = {
  question: "",
  answer: "",
  priority: 0,
  isActive: true,
};

const INITIAL = {
  title: "",
  description: "",
  merchant: "",
  features: [],
  pricingOptions: [INITIAL_OPTION],
  faqs: [],
  status: "DRAFT",
  priority: 100,
  notes: "",
};

function FormView({ isUpdate, merchantCode }) {
  const dispatch = useDispatch();
  const subscriptionInfo = useSelector((state) => state.subscriptions.subscriptionInfo);
  const platformList = useSelector((state) => state.platforms.platformList || []);
  const currencyList = useSelector((state) => state.common.currencyList || []);
  const [initialValues, setInitialValues] = useState(INITIAL);
  const [isFaqModalOpen, setIsFaqModalOpen] = useState(false);
  const [editingFaqIndex, setEditingFaqIndex] = useState(null);
  const [faqFormValues, setFaqFormValues] = useState({
    question: "",
    answer: "",
    priority: 0,
    isActive: true,
  });

  useEffect(() => {
    dispatch(getPlatformsList({ page: 1, size: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (isUpdate && subscriptionInfo && subscriptionInfo._id) {
      setInitialValues({
        title: subscriptionInfo.title || "",
        description: subscriptionInfo.description || "",
        merchant: subscriptionInfo.merchant || merchantCode || "",
        features: Array.isArray(subscriptionInfo.features) ? subscriptionInfo.features : [],
        status: subscriptionInfo.status || "DRAFT",
        priority: subscriptionInfo.priority ?? 100,
        notes: subscriptionInfo.notes || "",
        pricingOptions: subscriptionInfo.pricingOptions && subscriptionInfo.pricingOptions.length > 0
          ? subscriptionInfo.pricingOptions.map(o => ({
              name: o.name || "",
              priceType: o.priceType || (o.price === null || o.price === undefined ? "CONTACT_SALES" : o.price === 0 ? "FREE" : "PAID"),
              price: o.price ?? "",
              currency: o.currency || "INR",
              billingCycle: o.billingCycle || "MONTHLY",
              customCycleDays: o.customCycleDays ?? "",
              trialPeriodDays: o.trialPeriodDays ?? 0,
              guaranteePeriodDays: o.guaranteePeriodDays ?? 0,
              buttonText: o.buttonText || "",
              ctaLink: o.ctaLink || "",
              subtext: o.subtext || "",
              featuresHeader: o.featuresHeader || "",
              isPopular: !!o.isPopular,
              features: Array.isArray(o.features) ? o.features : [],
              isActive: o.isActive !== false,
              discountType: o.discountType ?? (subscriptionInfo.discountType || ""),
              discountValue: o.discountValue ?? (subscriptionInfo.discountValue ?? ""),
              startAt: o.startAt
                ? new Date(o.startAt).toISOString().slice(0, 16)
                : (subscriptionInfo.startAt ? new Date(subscriptionInfo.startAt).toISOString().slice(0, 16) : ""),
              endAt: o.endAt
                ? new Date(o.endAt).toISOString().slice(0, 16)
                : (subscriptionInfo.endAt ? new Date(subscriptionInfo.endAt).toISOString().slice(0, 16) : ""),
            }))
          : [INITIAL_OPTION],
        faqs: subscriptionInfo.faqs && subscriptionInfo.faqs.length > 0
          ? subscriptionInfo.faqs.map(f => ({
              question: f.question || "",
              answer: f.answer || "",
              priority: f.priority ?? 0,
              isActive: f.isActive !== false,
            }))
          : [],
      });
    } else {
      setInitialValues({
        ...INITIAL,
        merchant: merchantCode || "",
      });
    }
  }, [isUpdate, subscriptionInfo, merchantCode]);

  const schema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    merchant: Yup.string().required("Merchant is required"),
    priority: Yup.number()
      .transform((value, originalValue) => (originalValue === "" || originalValue === null) ? null : value)
      .nullable()
      .min(0)
      .optional(),
    features: Yup.array().of(Yup.string().required("Feature text is required")).optional(),
    notes: Yup.string().optional().nullable(),
    pricingOptions: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required("Option name is required"),
        priceType: Yup.string().oneOf(["PAID", "FREE", "CONTACT_SALES"]).required(),
        price: Yup.number()
          .min(0, "Price must be ≥ 0")
          .transform((value, originalValue) => (originalValue === "" || originalValue === null) ? null : value)
          .nullable()
          .when("priceType", {
            is: "PAID",
            then: (n) => n.required("Price is required"),
          }),
        currency: Yup.string().when("priceType", {
          is: "PAID",
          then: (s) => s.required("Currency is required"),
          otherwise: (s) => s.optional().nullable(),
        }),
        billingCycle: Yup.string().oneOf(["MONTHLY", "QUARTERLY", "YEARLY", "CUSTOM"]).required(),
        customCycleDays: Yup.number()
          .transform((value, originalValue) => (originalValue === "" || originalValue === null) ? null : value)
          .nullable()
          .when("billingCycle", {
            is: "CUSTOM",
            then: (n) => n.min(1, "Must be ≥ 1").required("Required for Custom cycle"),
          }),
        trialPeriodDays: Yup.number()
          .transform((value, originalValue) => (originalValue === "" || originalValue === null) ? null : value)
          .nullable()
          .min(0)
          .optional(),
        guaranteePeriodDays: Yup.number()
          .transform((value, originalValue) => (originalValue === "" || originalValue === null) ? null : value)
          .nullable()
          .min(0)
          .optional(),
        buttonText: Yup.string().optional().nullable(),
        ctaLink: Yup.string()
          .transform((value, originalValue) => originalValue === "" ? null : value)
          .url("Must be a valid URL")
          .optional()
          .nullable(),
        subtext: Yup.string().optional().nullable(),
        featuresHeader: Yup.string().optional().nullable(),
        features: Yup.array().of(Yup.string().required("Feature text is required")).optional(),
        discountType: Yup.string().oneOf(["PERCENT", "FLAT", ""]).optional().nullable(),
        discountValue: Yup.number()
          .transform((value, originalValue) => (originalValue === "" || originalValue === null) ? null : value)
          .nullable()
          .min(0)
          .optional(),
        startAt: Yup.string().optional().nullable(),
        endAt: Yup.string().optional().nullable(),
      })
    ).min(1, "At least one pricing option is required"),
    faqs: Yup.array().of(
      Yup.object().shape({
        question: Yup.string().required("Question is required"),
        answer: Yup.string().required("Answer is required"),
        priority: Yup.number()
          .transform((value, originalValue) => (originalValue === "" || originalValue === null) ? null : value)
          .nullable()
          .optional(),
      })
    ),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: schema,
    onSubmit: (values) => {
      const payload = {
        title: values.title,
        description: values.description,
        merchant: values.merchant,
        priority: Number(values.priority) || 100,
        status: values.status,
        features: (values.features || []).map(f => f.trim()).filter(Boolean),
        notes: values.notes || "",
        pricingOptions: values.pricingOptions.map((o) => ({
          name: o.name,
          priceType: o.priceType,
          price: o.priceType === "FREE" ? 0 : o.priceType === "CONTACT_SALES" ? null : Number(o.price),
          currency: o.priceType === "PAID" ? o.currency : undefined,
          billingCycle: o.billingCycle,
          customCycleDays: o.billingCycle === "CUSTOM" ? Number(o.customCycleDays) : undefined,
          trialPeriodDays: Number(o.trialPeriodDays) || 0,
          guaranteePeriodDays: Number(o.guaranteePeriodDays) || 0,
          buttonText: o.buttonText || "",
          ctaLink: o.ctaLink || null,
          subtext: o.subtext || "",
          featuresHeader: o.featuresHeader || "",
          isPopular: !!o.isPopular,
          features: (o.features || []).map(f => f.trim()).filter(Boolean),
          isActive: o.isActive,
          discountType: o.discountType || null,
          discountValue: o.discountValue !== "" && o.discountValue !== null ? Number(o.discountValue) : null,
          startAt: o.startAt || null,
          endAt: o.endAt || null,
        })),
        faqs: values.faqs.map((f) => ({
          question: f.question,
          answer: f.answer,
          priority: Number(f.priority) || 0,
          isActive: f.isActive,
        })),
      };

      if (isUpdate && subscriptionInfo?._id) {
        dispatch(updateSubscription(subscriptionInfo._id, payload))
          .then(() => {
            dispatch(getSubscriptionsList(merchantCode ? { merchant: merchantCode } : {})).catch(() => {});
            dispatch(hideDialogCommon());
          })
          .catch(() => {});
      } else {
        dispatch(createSubscription(payload))
          .then(() => {
            dispatch(getSubscriptionsList(merchantCode ? { merchant: merchantCode } : {})).catch(() => {});
            dispatch(hideDialogCommon());
          })
          .catch(() => {});
      }
    },
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    getFieldProps,
    setFieldValue,
  } = formik;

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      console.warn("Subscription Form Validation Errors:", errors);
    }
  }, [errors]);

  const merchants = platformList || [];

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={handleSubmit} className="signin-form px-2">
        <div className="row g-3">
          {/* General Info */}
          <div className="col-md-6">
            <div className="form-group mb-3">
              <label className="col-form-label pb-1" style={{ fontWeight: 500, fontSize: "14px", color: "#374151" }}>
                Subscription Title <span className="text-danger">*</span>
              </label>
              <input
                className="form-control"
                type="text"
                placeholder="e.g. Premium Learning Pass"
                style={{ borderRadius: "6px", border: "1px solid #d1d5db", padding: "10px 12px" }}
                {...getFieldProps("title")}
              />
              {touched.title && errors.title && (
                <p className="error-msg text-danger mt-1 mb-0" style={{ fontSize: "12px", fontWeight: 500 }}>{errors.title}</p>
              )}
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group mb-3">
              <label className="col-form-label pb-1" style={{ fontWeight: 500, fontSize: "14px", color: "#374151" }}>
                Merchant <span className="text-danger">*</span>
              </label>
              <select 
                className="form-control form-select" 
                style={{ borderRadius: "6px", border: "1px solid #d1d5db", padding: "10px 12px" }}
                disabled={!!merchantCode}
                {...getFieldProps("merchant")}
              >
                <option value="">Select merchant</option>
                {merchants.map((m) => (
                  <option key={m._id || m.code} value={m.code}>
                    {m.name}
                  </option>
                ))}
              </select>
              {touched.merchant && errors.merchant && (
                <p className="error-msg text-danger mt-1 mb-0" style={{ fontSize: "12px", fontWeight: 500 }}>{errors.merchant}</p>
              )}
            </div>
          </div>
          <div className="col-md-12">
            <div className="form-group mb-3">
              <label className="col-form-label pb-1" style={{ fontWeight: 500, fontSize: "14px", color: "#374151" }}>Description</label>
              <textarea
                className="form-control"
                rows={2}
                placeholder="Brief description of what this subscription offers..."
                style={{ borderRadius: "6px", border: "1px solid #d1d5db", padding: "10px 12px" }}
                {...getFieldProps("description")}
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group mb-3">
              <label className="col-form-label pb-1" style={{ fontWeight: 500, fontSize: "14px", color: "#374151" }}>Status</label>
              <select 
                className="form-control form-select" 
                style={{ borderRadius: "6px", border: "1px solid #d1d5db", padding: "10px 12px" }}
                {...getFieldProps("status")}
              >
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group mb-3">
              <label className="col-form-label pb-1" style={{ fontWeight: 500, fontSize: "14px", color: "#374151" }}>Priority</label>
              <input
                className="form-control"
                type="number"
                min={0}
                style={{ borderRadius: "6px", border: "1px solid #d1d5db", padding: "10px 12px" }}
                {...getFieldProps("priority")}
              />
              {touched.priority && errors.priority && (
                <p className="error-msg text-danger mt-1 mb-0" style={{ fontSize: "12px", fontWeight: 500 }}>{errors.priority}</p>
              )}
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group mb-3">
              <label className="col-form-label pb-1" style={{ fontWeight: 500, fontSize: "14px", color: "#374151" }}>Global Features</label>
              <FieldArray name="features">
                {({ push, remove }) => (
                  <div>
                    {(values.features || []).map((feature, idx) => (
                      <div key={idx} className="mb-2">
                        <div className="d-flex align-items-center gap-2">
                          <input
                            className="form-control"
                            type="text"
                            placeholder="e.g. Free Certificates"
                            style={{ borderRadius: "6px", border: "1px solid #d1d5db", padding: "10px 12px" }}
                            {...getFieldProps(`features.${idx}`)}
                          />
                          <button
                            type="button"
                            className="btn btn-sm text-danger border-0 p-0"
                            onClick={() => remove(idx)}
                            style={{ background: "transparent" }}
                          >
                            <i className="fa fa-trash" />
                          </button>
                        </div>
                        {touched.features?.[idx] && errors.features?.[idx] && (
                          <p className="error-msg text-danger mt-1 mb-0" style={{ fontSize: "11px", fontWeight: 500 }}>{errors.features[idx]}</p>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => push("")}
                      style={{ borderRadius: "4px", fontSize: "12px", padding: "4px 8px" }}
                    >
                      <i className="fa fa-plus mr-1" /> Add Feature
                    </button>
                  </div>
                )}
              </FieldArray>
            </div>
          </div>

          {/* Pricing Options Section */}
          <div className="col-md-12 mt-4">
            <div className="d-flex align-items-center justify-content-between mb-3" style={{ borderBottom: "2px solid #f3f4f6", paddingBottom: "12px" }}>
              <h5 className="m-0" style={{ fontWeight: 600, color: "#111827", fontSize: "16px" }}>
                Pricing Options
              </h5>
            </div>
            <FieldArray name="pricingOptions">
              {({ push, remove }) => (
                <div>
                  {values.pricingOptions.map((opt, index) => (
                    <div
                      key={index}
                      className="card border-0 mb-4 shadow-sm"
                      style={{
                        backgroundColor: "#f9fafb",
                        border: "1px solid #e5e7eb",
                        borderRadius: "10px",
                        padding: "20px",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <span style={{ fontWeight: 600, color: "#4b5563", fontSize: "14px" }}>
                          <i className="fa fa-tag text-primary mr-1" /> Option #{index + 1}
                        </span>
                        {values.pricingOptions.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-sm text-danger border-0 p-0"
                            style={{ fontWeight: 500, fontSize: "13px", gap: "4px", backgroundColor: "transparent" }}
                            onClick={() => remove(index)}
                          >
                            <i className="fa fa-trash" /> Remove Option
                          </button>
                        )}
                      </div>
                      <div className="row g-3">
                        <div className="col-md-3">
                          <div className="form-group mb-2">
                            <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563" }}>Option Name <span className="text-danger">*</span></label>
                            <input
                              className="form-control form-control-sm"
                              type="text"
                              placeholder="e.g. Monthly, Annual"
                              style={{ borderRadius: "4px", border: "1px solid #d1d5db" }}
                              {...getFieldProps(`pricingOptions.${index}.name`)}
                            />
                            {touched.pricingOptions?.[index]?.name && errors.pricingOptions?.[index]?.name && (
                              <p className="error-msg text-danger style-sm mt-1 mb-0" style={{ fontSize: "11px", fontWeight: 500 }}>{errors.pricingOptions[index].name}</p>
                            )}
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group mb-2">
                            <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563" }}>Price Type</label>
                            <select
                              className="form-control form-control-sm form-select"
                              style={{ borderRadius: "4px", border: "1px solid #d1d5db" }}
                              {...getFieldProps(`pricingOptions.${index}.priceType`)}
                            >
                              <option value="PAID">Paid</option>
                              <option value="FREE">Free (No Price)</option>
                              <option value="CONTACT_SALES">Contact Sales</option>
                            </select>
                          </div>
                        </div>
                        {opt.priceType === "PAID" ? (
                          <>
                            <div className="col-md-2">
                              <div className="form-group mb-2">
                                <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563" }}>Price <span className="text-danger">*</span></label>
                                <input
                                  className="form-control form-control-sm"
                                  type="number"
                                  min={0}
                                  step="any"
                                  placeholder="0.00"
                                  style={{ borderRadius: "4px", border: "1px solid #d1d5db" }}
                                  {...getFieldProps(`pricingOptions.${index}.price`)}
                                />
                                {touched.pricingOptions?.[index]?.price && errors.pricingOptions?.[index]?.price && (
                                  <p className="error-msg text-danger style-sm mt-1 mb-0" style={{ fontSize: "11px", fontWeight: 500 }}>{errors.pricingOptions[index].price}</p>
                                )}
                              </div>
                            </div>
                            <div className="col-md-2">
                              <div className="form-group mb-2">
                                <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563" }}>Currency</label>
                                <select
                                  className="form-control form-control-sm form-select"
                                  style={{ borderRadius: "4px", border: "1px solid #d1d5db" }}
                                  {...getFieldProps(`pricingOptions.${index}.currency`)}
                                >
                                  {currencyList.map((c) => (
                                    <option key={c.currency} value={c.currency}>
                                      {c.currency}
                                    </option>
                                  ))}
                                </select>
                                {touched.pricingOptions?.[index]?.currency && errors.pricingOptions?.[index]?.currency && (
                                  <p className="error-msg text-danger style-sm mt-1 mb-0" style={{ fontSize: "11px", fontWeight: 500 }}>{errors.pricingOptions[index].currency}</p>
                                )}
                              </div>
                            </div>
                            <div className="col-md-2">
                              <div className="form-group mb-2">
                                <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563" }}>Billing Cycle</label>
                                <select
                                  className="form-control form-control-sm form-select"
                                  style={{ borderRadius: "4px", border: "1px solid #d1d5db" }}
                                  {...getFieldProps(`pricingOptions.${index}.billingCycle`)}
                                >
                                  {BILLING_CYCLES.map((bc) => (
                                    <option key={bc.value} value={bc.value}>
                                      {bc.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="col-md-6">
                            <div className="form-group mb-2">
                              <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563" }}>Billing Cycle</label>
                              <select
                                className="form-control form-control-sm form-select"
                                style={{ borderRadius: "4px", border: "1px solid #d1d5db" }}
                                {...getFieldProps(`pricingOptions.${index}.billingCycle`)}
                              >
                                {BILLING_CYCLES.map((bc) => (
                                  <option key={bc.value} value={bc.value}>
                                    {bc.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )}
                        {opt.billingCycle === "CUSTOM" && (
                          <div className="col-md-3">
                            <div className="form-group mb-2">
                              <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563" }}>Cycle Days <span className="text-danger">*</span></label>
                              <input
                                className="form-control form-control-sm"
                                type="number"
                                min={1}
                                style={{ borderRadius: "4px", border: "1px solid #d1d5db" }}
                                {...getFieldProps(`pricingOptions.${index}.customCycleDays`)}
                              />
                              {touched.pricingOptions?.[index]?.customCycleDays && errors.pricingOptions?.[index]?.customCycleDays && (
                                <p className="error-msg text-danger style-sm mt-1 mb-0" style={{ fontSize: "11px", fontWeight: 500 }}>{errors.pricingOptions[index].customCycleDays}</p>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="col-md-3">
                          <div className="form-group mb-2">
                            <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563" }}>Trial Period (Days)</label>
                            <input
                              className="form-control form-control-sm"
                              type="number"
                              min={0}
                              style={{ borderRadius: "4px", border: "1px solid #d1d5db" }}
                              {...getFieldProps(`pricingOptions.${index}.trialPeriodDays`)}
                            />
                            {touched.pricingOptions?.[index]?.trialPeriodDays && errors.pricingOptions?.[index]?.trialPeriodDays && (
                              <p className="error-msg text-danger style-sm mt-1 mb-0" style={{ fontSize: "11px", fontWeight: 500 }}>{errors.pricingOptions[index].trialPeriodDays}</p>
                            )}
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group mb-2">
                            <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563" }}>Guarantee Period (Days)</label>
                            <input
                              className="form-control form-control-sm"
                              type="number"
                              min={0}
                              style={{ borderRadius: "4px", border: "1px solid #d1d5db" }}
                              {...getFieldProps(`pricingOptions.${index}.guaranteePeriodDays`)}
                            />
                            {touched.pricingOptions?.[index]?.guaranteePeriodDays && errors.pricingOptions?.[index]?.guaranteePeriodDays && (
                              <p className="error-msg text-danger style-sm mt-1 mb-0" style={{ fontSize: "11px", fontWeight: 500 }}>{errors.pricingOptions[index].guaranteePeriodDays}</p>
                            )}
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group mb-2">
                            <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563" }}>Button Text</label>
                            <input
                              className="form-control form-control-sm"
                              type="text"
                              placeholder="e.g. Subscribe Now"
                              style={{ borderRadius: "4px", border: "1px solid #d1d5db" }}
                              {...getFieldProps(`pricingOptions.${index}.buttonText`)}
                            />
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group mb-2">
                            <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563" }}>CTA Link (URL)</label>
                            <input
                              className="form-control form-control-sm"
                              type="text"
                              placeholder="https://..."
                              style={{ borderRadius: "4px", border: "1px solid #d1d5db" }}
                              {...getFieldProps(`pricingOptions.${index}.ctaLink`)}
                            />
                            {touched.pricingOptions?.[index]?.ctaLink && errors.pricingOptions?.[index]?.ctaLink && (
                              <p className="error-msg text-danger style-sm mt-1 mb-0" style={{ fontSize: "11px", fontWeight: 500 }}>{errors.pricingOptions[index].ctaLink}</p>
                            )}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group mb-2">
                            <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563" }}>Subtext</label>
                            <input
                              className="form-control form-control-sm"
                              type="text"
                              placeholder="e.g. Billed annually"
                              style={{ borderRadius: "4px", border: "1px solid #d1d5db" }}
                              {...getFieldProps(`pricingOptions.${index}.subtext`)}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group mb-2">
                            <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563" }}>Features Header</label>
                            <input
                              className="form-control form-control-sm"
                              type="text"
                              placeholder="e.g. Everything in Free, plus:"
                              style={{ borderRadius: "4px", border: "1px solid #d1d5db" }}
                              {...getFieldProps(`pricingOptions.${index}.featuresHeader`)}
                            />
                          </div>
                        </div>
                        <div className="col-md-3 col-sm-6">
                          <div className="form-group mb-2">
                            <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563", display: "block" }}>Is Popular?</label>
                            <div className="form-check form-switch mt-1 pt-1">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                style={{ cursor: "pointer" }}
                                checked={opt.isPopular}
                                {...getFieldProps(`pricingOptions.${index}.isPopular`)}
                              />
                              <label className="form-check-label" style={{ fontSize: "12px", color: "#6b7280", marginLeft: "6px" }}>
                                {opt.isPopular ? "Yes" : "No"}
                              </label>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-3 col-sm-6">
                          <div className="form-group mb-2">
                            <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563", display: "block" }}>Option Active Status</label>
                            <div className="form-check form-switch mt-1 pt-1">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                style={{ cursor: "pointer" }}
                                checked={opt.isActive}
                                {...getFieldProps(`pricingOptions.${index}.isActive`)}
                              />
                              <label className="form-check-label" style={{ fontSize: "12px", color: "#6b7280", marginLeft: "6px" }}>
                                {opt.isActive ? "Active" : "Inactive"}
                              </label>
                            </div>
                          </div>
                        </div>
                        {/* Option Discount section */}
                        <div className="col-md-12">
                          <div style={{ backgroundColor: "#f3f4f6", padding: "12px 16px", borderRadius: "8px", border: "1px solid #e5e7eb", marginBottom: "8px" }}>
                            <div style={{ fontWeight: 600, color: "#374151", fontSize: "13px", marginBottom: "8px" }}>
                              <i className="fa fa-percent text-success mr-1" /> Option Discount (optional)
                            </div>
                            <div className="row g-2">
                              <div className="col-md-3">
                                <div className="form-group mb-0">
                                  <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563" }}>Discount Type</label>
                                  <select 
                                    className="form-control form-control-sm form-select" 
                                    style={{ borderRadius: "4px", border: "1px solid #d1d5db" }}
                                    {...getFieldProps(`pricingOptions.${index}.discountType`)}
                                  >
                                    <option value="">No Discount</option>
                                    {DISCOUNT_TYPES.map((dt) => (
                                      <option key={dt.value} value={dt.value}>
                                        {dt.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                              {opt.discountType && (
                                <>
                                  <div className="col-md-3">
                                    <div className="form-group mb-0">
                                      <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563" }}>Discount Value</label>
                                      <input
                                        className="form-control form-control-sm"
                                        type="number"
                                        min={0}
                                        step="any"
                                        placeholder="e.g. 10"
                                        style={{ borderRadius: "4px", border: "1px solid #d1d5db" }}
                                        {...getFieldProps(`pricingOptions.${index}.discountValue`)}
                                      />
                                      {touched.pricingOptions?.[index]?.discountValue && errors.pricingOptions?.[index]?.discountValue && (
                                        <p className="error-msg text-danger style-sm mt-1 mb-0" style={{ fontSize: "11px", fontWeight: 500 }}>{errors.pricingOptions[index].discountValue}</p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="col-md-3">
                                    <div className="form-group mb-0">
                                      <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563" }}>Start at</label>
                                      <input
                                        className="form-control form-control-sm"
                                        type="datetime-local"
                                        style={{ borderRadius: "4px", border: "1px solid #d1d5db" }}
                                        {...getFieldProps(`pricingOptions.${index}.startAt`)}
                                      />
                                    </div>
                                  </div>
                                  <div className="col-md-3">
                                    <div className="form-group mb-0">
                                      <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563" }}>End at</label>
                                      <input
                                        className="form-control form-control-sm"
                                        type="datetime-local"
                                        style={{ borderRadius: "4px", border: "1px solid #d1d5db" }}
                                        {...getFieldProps(`pricingOptions.${index}.endAt`)}
                                      />
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="form-group mb-2">
                            <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563" }}>Option Features</label>
                            <FieldArray name={`pricingOptions.${index}.features`}>
                              {({ push, remove }) => (
                                <div>
                                  {(opt.features || []).map((feature, fIdx) => (
                                    <div key={fIdx} className="mb-2">
                                      <div className="d-flex align-items-center gap-2">
                                        <input
                                          className="form-control form-control-sm"
                                          type="text"
                                          placeholder="e.g. 1 on 1 support"
                                          style={{ borderRadius: "4px", border: "1px solid #d1d5db" }}
                                          {...getFieldProps(`pricingOptions.${index}.features.${fIdx}`)}
                                        />
                                        <button
                                          type="button"
                                          className="btn btn-sm text-danger border-0 p-0"
                                          onClick={() => remove(fIdx)}
                                          style={{ background: "transparent" }}
                                        >
                                          <i className="fa fa-trash" />
                                        </button>
                                      </div>
                                      {touched.pricingOptions?.[index]?.features?.[fIdx] && errors.pricingOptions?.[index]?.features?.[fIdx] && (
                                        <p className="error-msg text-danger style-sm mt-1 mb-0" style={{ fontSize: "11px", fontWeight: 500 }}>{errors.pricingOptions[index].features[fIdx]}</p>
                                      )}
                                    </div>
                                  ))}
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => push("")}
                                    style={{ borderRadius: "4px", fontSize: "11px", padding: "3px 6px" }}
                                  >
                                    <i className="fa fa-plus mr-1" /> Add Feature
                                  </button>
                                </div>
                              )}
                            </FieldArray>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm mb-3 d-flex align-items-center"
                    style={{ borderRadius: "6px", gap: "6px", padding: "6px 12px", fontWeight: 500 }}
                    onClick={() => push(INITIAL_OPTION)}
                  >
                    <i className="fa fa-plus" /> Add New Pricing Option
                  </button>
                </div>
              )}
            </FieldArray>
          </div>

          <div className="col-md-12 mt-3">
            <div className="form-group mb-3">
              <label className="col-form-label pb-1" style={{ fontWeight: 500, fontSize: "14px", color: "#374151" }}>Internal Notes (optional)</label>
              <textarea
                className="form-control"
                rows={2}
                placeholder="Add private internal notes here..."
                style={{ borderRadius: "6px", border: "1px solid #d1d5db", padding: "10px 12px" }}
                {...getFieldProps("notes")}
              />
            </div>
          </div>

          {/* Embedded FAQs Section */}
          <div className="col-md-12 mt-4">
            <div className="d-flex align-items-center justify-content-between mb-3" style={{ borderBottom: "2px solid #f3f4f6", paddingBottom: "12px" }}>
              <h5 className="m-0" style={{ fontWeight: 600, color: "#111827", fontSize: "16px" }}>
                Subscription FAQs
              </h5>
              <Button
                type="primary"
                size="small"
                htmlType="button"
                onClick={() => {
                  setEditingFaqIndex(null);
                  setFaqFormValues({ question: "", answer: "", priority: 0, isActive: true });
                  setIsFaqModalOpen(true);
                }}
              >
                <i className="fa fa-plus mr-1" /> Add FAQ
              </Button>
            </div>

            <Table
              dataSource={values.faqs || []}
              rowKey={(record, idx) => idx}
              pagination={false}
              size="small"
              columns={[
                {
                  title: "#",
                  render: (_, __, idx) => idx + 1,
                  width: 50,
                },
                {
                  title: "Question",
                  dataIndex: "question",
                  render: (q) => q.length > 60 ? `${q.substring(0, 60)}...` : q,
                },
                {
                  title: "Priority",
                  dataIndex: "priority",
                  width: 80,
                  align: "center",
                },
                {
                  title: "Status",
                  dataIndex: "isActive",
                  width: 100,
                  align: "center",
                  render: (active) => (
                    <Tag color={active ? "success" : "default"}>{active ? "Active" : "Inactive"}</Tag>
                  ),
                },
                {
                  title: "Actions",
                  width: 120,
                  align: "center",
                  render: (_, record, idx) => (
                    <Space size="small">
                      <Button
                        type="text"
                        size="small"
                        htmlType="button"
                        onClick={() => {
                          setEditingFaqIndex(idx);
                          setFaqFormValues({
                            question: record.question || "",
                            answer: record.answer || "",
                            priority: record.priority ?? 0,
                            isActive: record.isActive !== false,
                          });
                          setIsFaqModalOpen(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        type="text"
                        danger
                        size="small"
                        htmlType="button"
                        onClick={() => {
                          const updated = [...values.faqs];
                          updated.splice(idx, 1);
                          setFieldValue("faqs", updated);
                        }}
                      >
                        Delete
                      </Button>
                    </Space>
                  ),
                },
              ]}
            />

            <Modal
              title={editingFaqIndex !== null ? "Edit FAQ" : "Add FAQ"}
              open={isFaqModalOpen}
              zIndex={2000}
              onCancel={() => setIsFaqModalOpen(false)}
              onOk={() => {
                if (!faqFormValues.question || !faqFormValues.answer) {
                  alert("Question and Answer are required.");
                  return;
                }
                const updated = [...(values.faqs || [])];
                if (editingFaqIndex !== null) {
                  updated[editingFaqIndex] = faqFormValues;
                } else {
                  updated.push(faqFormValues);
                }
                setFieldValue("faqs", updated);
                setIsFaqModalOpen(false);
              }}
              okText="Save FAQ"
            >
              <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "16px" }}>
                <div>
                  <label style={{ fontWeight: 500, display: "block", marginBottom: "4px" }}>Question <span className="text-danger">*</span></label>
                  <Input
                    placeholder="e.g. What payment methods are supported?"
                    value={faqFormValues.question}
                    onChange={(e) => setFaqFormValues({ ...faqFormValues, question: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ fontWeight: 500, display: "block", marginBottom: "4px" }}>Answer <span className="text-danger">*</span></label>
                  <Input.TextArea
                    rows={4}
                    placeholder="e.g. We accept all major credit cards, UPI, and PayPal..."
                    value={faqFormValues.answer}
                    onChange={(e) => setFaqFormValues({ ...faqFormValues, answer: e.target.value })}
                  />
                </div>
                <div style={{ display: "flex", gap: "16px" }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontWeight: 500, display: "block", marginBottom: "4px" }}>Priority</label>
                    <Input
                      type="number"
                      min={0}
                      value={faqFormValues.priority}
                      onChange={(e) => setFaqFormValues({ ...faqFormValues, priority: Number(e.target.value) || 0 })}
                    />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                    <label style={{ fontWeight: 500, marginBottom: "8px" }}>Active Status</label>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", height: "32px" }}>
                      <Switch
                        checked={faqFormValues.isActive}
                        onChange={(checked) => setFaqFormValues({ ...faqFormValues, isActive: checked })}
                      />
                      <span>{faqFormValues.isActive ? "Active" : "Inactive"}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Modal>
          </div>
        </div>
        <div className="d-flex justify-content-end gap-2 mt-4 pt-3" style={{ borderTop: "1px solid #e5e7eb" }}>
          <button 
            type="submit" 
            className="btn btn-primary px-4 py-2" 
            style={{ borderRadius: "6px", fontWeight: 500, minWidth: "120px" }}
          >
            {isUpdate && subscriptionInfo?._id ? "Save Changes" : "Create Subscription"}
          </button>
        </div>
      </Form>
    </FormikProvider>
  );
}

export default FormView;
